/**
 * Build Information Utilities
 * Provides access to build-time information for debugging and support
 */

export interface BuildInfo {
  version: string;
  commit: string;
  shortCommit: string;
  branch: string;
  buildTime: string;
  environment: string;
}

/**
 * Get build information injected at build time
 */
export function getBuildInfo(): BuildInfo {
  const fallbackInfo = {
    version: 'development',
    commit: 'unknown',
    shortCommit: 'unknown',
    branch: 'unknown',
    buildTime: new Date().toISOString(),
    environment: 'development',
  };

  try {
    // Try to get from build-time injection
    if (typeof __GIT_INFO__ !== 'undefined' && typeof __RELEASE_VERSION__ !== 'undefined') {
      return {
        version: __RELEASE_VERSION__,
        commit: __GIT_INFO__.commit,
        shortCommit: __GIT_INFO__.shortCommit,
        branch: __GIT_INFO__.branch,
        buildTime: __GIT_INFO__.timestamp,
        environment: import.meta.env.MODE || 'development',
      };
    }
  } catch (error) {
    console.warn('Failed to get build-time information:', error);
  }

  return fallbackInfo;
}

/**
 * Initialize global build information for debugging
 * Attaches build info to window object in development
 */
export function initBuildInfo(): void {
  const buildInfo = getBuildInfo();

  // Attach to window for debugging in development
  if (import.meta.env.DEV) {
    window.__LEGALSYNCUP_BUILD_INFO__ = {
      version: buildInfo.version,
      commit: buildInfo.shortCommit,
      buildTime: buildInfo.buildTime,
      branch: buildInfo.branch,
    };

    console.log('🏗️  LegalSyncUp Build Info:', buildInfo);
  }
}

/**
 * Log build information to Sentry context
 */
export function setBuildContextForSentry(): void {
  const buildInfo = getBuildInfo();

  // Import and use Sentry directly
  import('@sentry/react').then(Sentry => {
    Sentry.setContext('build', {
      version: buildInfo.version,
      commit: buildInfo.shortCommit,
      branch: buildInfo.branch,
      buildTime: buildInfo.buildTime,
      environment: buildInfo.environment,
    });
  }).catch(error => {
    console.warn('Failed to set Sentry build context:', error);
  });
}